/**
 * PatrolExecutionPage - Phase 3: Patrol Plan Execution
 * Mobile-first interface for officers to execute patrol plans.
 * 
 * Flow: View assigned sites -> Start Execution -> Per-site check-in/check-out -> Complete
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  MapPin, Play, CheckCircle2, Clock, Loader2,
  AlertTriangle, ChevronRight, Target, Timer, Flag,
  Camera, ArrowLeft, SkipForward, AlertCircle,
  Building2, Navigation, Shield, XCircle, Check,
  Radio, StopCircle
} from 'lucide-react';

// Haversine distance
function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const dp = ((lat2 - lat1) * Math.PI) / 180;
  const dl = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(m) {
  if (m == null) return '--';
  return m < 1000 ? `${Math.round(m)}m` : `${(m / 1000).toFixed(1)}km`;
}

function formatDuration(mins) {
  if (mins == null) return '--';
  if (mins < 1) return '<1 min';
  if (mins < 60) return `${Math.round(mins)} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${m}m`;
}

function formatTime(iso) {
  if (!iso) return '--:--';
  return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
}

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: Clock },
  checked_in: { label: 'On Site', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Radio },
  checked_out: { label: 'Completed', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  skipped: { label: 'Skipped', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: SkipForward },
  exception: { label: 'Exception', color: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle },
};

export default function PatrolExecutionPage() {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();

  const [execution, setExecution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [gps, setGps] = useState(null);
  const [gpsStatus, setGpsStatus] = useState('idle');
  const gpsWatchRef = useRef(null);
  const elapsedRef = useRef(null);
  const [elapsed, setElapsed] = useState('--:--:--');

  // Dialogs
  const [skipDialog, setSkipDialog] = useState(null);
  const [exceptionDialog, setExceptionDialog] = useState(null);
  const [completeDialog, setCompleteDialog] = useState(false);
  const [geofenceOverrideDialog, setGeofenceOverrideDialog] = useState(null);
  const [skipReason, setSkipReason] = useState('');
  const [exceptionReason, setExceptionReason] = useState('');
  const [completeNotes, setCompleteNotes] = useState('');
  const [geofenceOverrideReason, setGeofenceOverrideReason] = useState('');

  // GPS watch
  const startGPS = useCallback(() => {
    if (!navigator.geolocation) { setGpsStatus('error'); return; }
    setGpsStatus('acquiring');
    gpsWatchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setGps({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGpsStatus('locked');
      },
      () => setGpsStatus('error'),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  }, []);

  useEffect(() => {
    startGPS();
    return () => { if (gpsWatchRef.current) navigator.geolocation.clearWatch(gpsWatchRef.current); };
  }, [startGPS]);

  // Fetch execution
  const fetchExecution = useCallback(async () => {
    if (!shiftId) return;
    try {
      setLoading(true);
      const res = await api.get(`/patrol-executions/by-shift/${shiftId}`);
      setExecution(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setExecution(null);
      } else {
        toast.error('Failed to load execution');
      }
    } finally {
      setLoading(false);
    }
  }, [api, shiftId]);

  useEffect(() => { fetchExecution(); }, [fetchExecution]);

  // Elapsed timer
  useEffect(() => {
    if (!execution?.started_at) return;
    const tick = () => {
      const start = new Date(execution.started_at);
      const diff = Math.floor((Date.now() - start) / 1000);
      const h = Math.floor(diff / 3600).toString().padStart(2, '0');
      const m = Math.floor((diff % 3600) / 60).toString().padStart(2, '0');
      const s = (diff % 60).toString().padStart(2, '0');
      setElapsed(`${h}:${m}:${s}`);
    };
    tick();
    elapsedRef.current = setInterval(tick, 1000);
    return () => clearInterval(elapsedRef.current);
  }, [execution?.started_at]);

  // Start execution
  const handleStart = async () => {
    try {
      setStarting(true);
      const res = await api.post('/patrol-executions/start', {
        shift_id: shiftId,
        device_id: navigator.userAgent?.slice(0, 50),
        gps: gps || undefined,
      });
      setExecution(res.data);
      toast.success('Patrol execution started');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start execution');
    } finally {
      setStarting(false);
    }
  };

  // Site check-in
  const handleCheckIn = async (siteId, overrideReason) => {
    if (!execution) return;
    try {
      setActionLoading(`checkin-${siteId}`);
      const payload = {
        gps: gps || undefined,
        device_id: navigator.userAgent?.slice(0, 50),
      };
      if (overrideReason) {
        payload.geofence_override_reason = overrideReason;
      }
      const res = await api.post(`/patrol-executions/${execution.id}/site/${siteId}/check-in`, payload);
      setExecution(res.data.execution);
      toast.success(`Checked in to ${res.data.site_name}`);
      if (res.data.geofence_override) {
        toast.warning('Geofence override recorded', { duration: 4000 });
      }
      if (res.data.late_arrival) {
        toast.warning(`Late arrival: +${Math.round(res.data.arrival_delay_minutes)} min`, { duration: 5000 });
      }
      setGeofenceOverrideDialog(null);
      setGeofenceOverrideReason('');
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail?.includes('geofence')) {
        setGeofenceOverrideDialog(siteId);
      } else {
        toast.error(err.response?.data?.detail || 'Check-in failed');
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Site check-out
  const handleCheckOut = async (siteId) => {
    if (!execution) return;
    try {
      setActionLoading(`checkout-${siteId}`);
      const res = await api.post(`/patrol-executions/${execution.id}/site/${siteId}/check-out`, {
        gps: gps || undefined,
        device_id: navigator.userAgent?.slice(0, 50),
      });
      setExecution(res.data.execution);
      toast.success(`Checked out from ${res.data.site_name} (${formatDuration(res.data.duration_minutes)})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-out failed');
    } finally {
      setActionLoading(null);
    }
  };

  // Skip site
  const handleSkip = async () => {
    if (!execution || !skipDialog) return;
    if (skipReason.trim().length < 10) {
      toast.error('Skip reason must be at least 10 characters');
      return;
    }
    try {
      setActionLoading(`skip-${skipDialog}`);
      const res = await api.post(`/patrol-executions/${execution.id}/site/${skipDialog}/skip`, {
        reason: skipReason.trim(),
        gps: gps || undefined,
      });
      setExecution(res.data.execution);
      toast.success('Site skipped');
      setSkipDialog(null);
      setSkipReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Skip failed');
    } finally {
      setActionLoading(null);
    }
  };

  // Exception
  const handleException = async () => {
    if (!execution || !exceptionDialog) return;
    if (exceptionReason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    try {
      setActionLoading(`exc-${exceptionDialog}`);
      const res = await api.post(`/patrol-executions/${execution.id}/site/${exceptionDialog}/exception`, {
        reason: exceptionReason.trim(),
        gps: gps || undefined,
      });
      setExecution(res.data.execution);
      toast.success('Exception recorded');
      setExceptionDialog(null);
      setExceptionReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record exception');
    } finally {
      setActionLoading(null);
    }
  };

  // Complete
  const handleComplete = async (force = false) => {
    if (!execution) return;
    const pending = (execution.site_visits || []).filter(v => ['pending', 'checked_in'].includes(v.status));
    if (pending.length > 0 && !force) {
      setCompleteDialog(true);
      return;
    }
    try {
      setActionLoading('complete');
      const res = await api.post(`/patrol-executions/${execution.id}/complete`, {
        gps: gps || undefined,
        notes: completeNotes || undefined,
        force_complete: force,
      });
      setExecution(res.data.execution);
      toast.success(`Patrol ${res.data.status === 'completed' ? 'completed' : 'finished with ' + res.data.status + ' status'}`);
      setCompleteDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete');
    } finally {
      setActionLoading(null);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="patrol-exec-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // No execution yet — show start screen
  if (!execution) {
    return (
      <div className="max-w-lg mx-auto p-4 space-y-6" data-testid="patrol-exec-start-screen">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-slate-800">Start Patrol</h1>
        </div>
        <Card className="border-blue-200 bg-blue-50/30">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-blue-100 flex items-center justify-center">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
            <p className="text-slate-600 text-sm">
              Start your patrol plan execution. Each site will need to be checked in and out.
            </p>
            <div className="flex items-center gap-2 justify-center text-xs text-slate-500">
              <Navigation className="h-3.5 w-3.5" />
              <span>GPS: {gpsStatus === 'locked' ? 'Active' : gpsStatus === 'acquiring' ? 'Acquiring...' : 'Unavailable'}</span>
            </div>
            <Button
              data-testid="start-patrol-execution-btn"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-base"
              onClick={handleStart}
              disabled={starting}
            >
              {starting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Play className="h-5 w-5 mr-2" />}
              Start Patrol Execution
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Active / Completed execution view
  const visits = execution.site_visits || [];
  const snap = execution.plan_snapshot || {};
  const summary = execution.completion_summary || {};
  const isActive = execution.status === 'in_progress';
  const isFinished = ['completed', 'partial', 'incomplete', 'abandoned', 'not_performed'].includes(execution.status);
  const completionPct = summary.completion_percentage || 0;
  const pendingSites = visits.filter(v => ['pending', 'checked_in'].includes(v.status));
  const doneSites = visits.filter(v => ['checked_out'].includes(v.status));

  return (
    <div className="max-w-lg mx-auto p-4 pb-24 space-y-4" data-testid="patrol-execution-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-slate-800 truncate" data-testid="patrol-exec-plan-name">
            {snap.name || 'Patrol Execution'}
          </h1>
          <p className="text-xs text-slate-500">{snap.region}</p>
        </div>
        <Badge
          data-testid="patrol-exec-status-badge"
          className={isActive ? 'bg-blue-100 text-blue-800' : isFinished ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}
        >
          {execution.status?.replace('_', ' ')}
        </Badge>
      </div>

      {/* Progress Bar */}
      <Card className="border-slate-200">
        <CardContent className="py-3 px-4 space-y-2">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span data-testid="patrol-exec-progress-label">{doneSites.length}/{visits.length} sites completed</span>
            <span>{Math.round(completionPct)}%</span>
          </div>
          <Progress value={completionPct} className="h-2" data-testid="patrol-exec-progress-bar" />
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1 text-slate-500">
              <Timer className="h-3.5 w-3.5" /> {elapsed}
            </span>
            {gps && (
              <span className="flex items-center gap-1 text-emerald-600">
                <Navigation className="h-3.5 w-3.5" /> GPS active
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Site List */}
      <div className="space-y-2" data-testid="patrol-exec-site-list">
        {visits.map((visit, idx) => {
          const cfg = STATUS_CONFIG[visit.status] || STATUS_CONFIG.pending;
          const StatusIcon = cfg.icon;
          const dist = gps && visit.site_latitude && visit.site_longitude
            ? haversineDistance(gps.latitude, gps.longitude, visit.site_latitude, visit.site_longitude)
            : null;

          return (
            <Card
              key={`${visit.site_id}-${idx}`}
              data-testid={`site-card-${visit.site_id}`}
              className={`border transition-all ${visit.status === 'checked_in' ? 'border-blue-400 ring-1 ring-blue-200 bg-blue-50/30' : 'border-slate-200'}`}
            >
              <CardContent className="py-3 px-4">
                <div className="flex items-start gap-3">
                  {/* Order badge */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                    ${visit.status === 'checked_out' ? 'bg-emerald-600 text-white' :
                      visit.status === 'checked_in' ? 'bg-blue-600 text-white' :
                      visit.status === 'skipped' ? 'bg-amber-500 text-white' :
                      visit.status === 'exception' ? 'bg-red-500 text-white' :
                      'bg-slate-200 text-slate-600'}`}>
                    {visit.status === 'checked_out' ? <Check className="h-4 w-4" /> : idx + 1}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold text-sm text-slate-800 truncate" data-testid={`site-name-${visit.site_id}`}>
                        {visit.site_name || 'Unknown Site'}
                      </h3>
                      <Badge className={`text-[10px] px-1.5 py-0 border ${cfg.color}`}>
                        <StatusIcon className="h-3 w-3 mr-0.5" />
                        {cfg.label}
                      </Badge>
                    </div>

                    {/* Meta info */}
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      {dist != null && (
                        <span className="flex items-center gap-0.5">
                          <MapPin className="h-3 w-3" /> {formatDistance(dist)}
                        </span>
                      )}
                      {visit.expected_duration_minutes && (
                        <span className="flex items-center gap-0.5">
                          <Clock className="h-3 w-3" /> ~{visit.expected_duration_minutes}min
                        </span>
                      )}
                      {visit.check_in_geofence_verified === true && (
                        <span className="text-emerald-600 flex items-center gap-0.5"><Target className="h-3 w-3" /> Verified</span>
                      )}
                      {visit.check_in_geofence_verified === false && (
                        <span className="text-amber-600 flex items-center gap-0.5"><AlertTriangle className="h-3 w-3" /> Outside fence</span>
                      )}
                      {visit.geofence_override_reason && (
                        <span className="text-amber-600 flex items-center gap-0.5"><Target className="h-3 w-3" /> Override</span>
                      )}
                      {visit.arrival_delay_minutes > 15 && (
                        <span className="text-red-600 flex items-center gap-0.5"><Timer className="h-3 w-3" /> +{Math.round(visit.arrival_delay_minutes)}min late</span>
                      )}
                    </div>

                    {/* Timestamps */}
                    {visit.check_in_at && (
                      <p className="text-xs text-slate-400 mt-1">In: {formatTime(visit.check_in_at)} {visit.check_out_at ? ` | Out: ${formatTime(visit.check_out_at)}` : ''} {visit.actual_duration_minutes ? ` (${formatDuration(visit.actual_duration_minutes)})` : ''}</p>
                    )}
                    {visit.skip_reason && <p className="text-xs text-amber-700 mt-1">Reason: {visit.skip_reason}</p>}
                    {visit.exception_reason && <p className="text-xs text-red-700 mt-1">Exception: {visit.exception_reason}</p>}

                    {/* Action buttons */}
                    {isActive && (
                      <div className="flex gap-2 mt-2">
                        {visit.status === 'pending' && (
                          <>
                            <Button
                              data-testid={`checkin-btn-${visit.site_id}`}
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs"
                              disabled={!!actionLoading}
                              onClick={() => handleCheckIn(visit.site_id)}
                            >
                              {actionLoading === `checkin-${visit.site_id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <MapPin className="h-3.5 w-3.5 mr-1" />}
                              Check In
                            </Button>
                            <Button
                              data-testid={`skip-btn-${visit.site_id}`}
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                              disabled={!!actionLoading}
                              onClick={() => setSkipDialog(visit.site_id)}
                            >
                              <SkipForward className="h-3.5 w-3.5 mr-1" /> Skip
                            </Button>
                            <Button
                              data-testid={`exception-btn-${visit.site_id}`}
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs text-red-700 border-red-300 hover:bg-red-50"
                              disabled={!!actionLoading}
                              onClick={() => setExceptionDialog(visit.site_id)}
                            >
                              <AlertCircle className="h-3.5 w-3.5 mr-1" /> Exception
                            </Button>
                          </>
                        )}
                        {visit.status === 'checked_in' && (
                          <>
                            <Button
                              data-testid={`checkout-btn-${visit.site_id}`}
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                              disabled={!!actionLoading}
                              onClick={() => handleCheckOut(visit.site_id)}
                            >
                              {actionLoading === `checkout-${visit.site_id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                              Check Out
                            </Button>
                            <Button
                              data-testid={`skip-checkedin-btn-${visit.site_id}`}
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                              disabled={!!actionLoading}
                              onClick={() => setSkipDialog(visit.site_id)}
                            >
                              <SkipForward className="h-3.5 w-3.5 mr-1" /> Skip
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Completion Summary (when finished) */}
      {isFinished && (
        <Card className="border-emerald-200 bg-emerald-50/30" data-testid="patrol-exec-completion-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-emerald-800">
              <Flag className="h-5 w-5" /> Patrol Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white/60 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-emerald-700">{summary.sites_completed || 0}</p>
                <p className="text-xs text-slate-500">Completed</p>
              </div>
              <div className="bg-white/60 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-amber-700">{summary.sites_skipped || 0}</p>
                <p className="text-xs text-slate-500">Skipped</p>
              </div>
              <div className="bg-white/60 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-red-700">{summary.sites_exception || 0}</p>
                <p className="text-xs text-slate-500">Exceptions</p>
              </div>
              <div className="bg-white/60 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-slate-700">{formatDuration(summary.total_duration_minutes)}</p>
                <p className="text-xs text-slate-500">Duration</p>
              </div>
            </div>
            <Button
              data-testid="back-to-dashboard-btn"
              className="w-full mt-2"
              variant="outline"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Complete Button (floating) */}
      {isActive && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur border-t border-slate-200 safe-area-bottom">
          <div className="max-w-lg mx-auto">
            <Button
              data-testid="complete-patrol-btn"
              className={`w-full h-12 text-base ${pendingSites.length === 0 ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-600 hover:bg-slate-700'} text-white`}
              disabled={actionLoading === 'complete'}
              onClick={() => handleComplete(false)}
            >
              {actionLoading === 'complete' ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <StopCircle className="h-5 w-5 mr-2" />}
              {pendingSites.length === 0 ? 'Complete Patrol' : `Complete (${pendingSites.length} site${pendingSites.length > 1 ? 's' : ''} remaining)`}
            </Button>
          </div>
        </div>
      )}

      {/* Skip Dialog */}
      <Dialog open={!!skipDialog} onOpenChange={(open) => { if (!open) { setSkipDialog(null); setSkipReason(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><SkipForward className="h-5 w-5 text-amber-600" /> Skip Site</DialogTitle>
            <DialogDescription>Provide a reason for skipping this site. This will be recorded for ops review.</DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="skip-reason-input"
            placeholder="Why is this site being skipped? (min 10 characters)"
            value={skipReason}
            onChange={e => setSkipReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSkipDialog(null); setSkipReason(''); }}>Cancel</Button>
            <Button
              data-testid="skip-confirm-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleSkip}
              disabled={skipReason.trim().length < 10 || !!actionLoading}
            >
              {actionLoading?.startsWith('skip') ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Skip Site
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exception Dialog */}
      <Dialog open={!!exceptionDialog} onOpenChange={(open) => { if (!open) { setExceptionDialog(null); setExceptionReason(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertCircle className="h-5 w-5 text-red-600" /> Report Exception</DialogTitle>
            <DialogDescription>Record an exception for this site. Link to an incident if applicable.</DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="exception-reason-input"
            placeholder="Describe the exception (min 10 characters)"
            value={exceptionReason}
            onChange={e => setExceptionReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setExceptionDialog(null); setExceptionReason(''); }}>Cancel</Button>
            <Button
              data-testid="exception-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleException}
              disabled={exceptionReason.trim().length < 10 || !!actionLoading}
            >
              {actionLoading?.startsWith('exc') ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Record Exception
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Complete Dialog */}
      <Dialog open={completeDialog} onOpenChange={setCompleteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Pending Sites</DialogTitle>
            <DialogDescription>
              {pendingSites.length} site{pendingSites.length > 1 ? 's are' : ' is'} still pending. Completing now will mark the patrol as incomplete for ops review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {pendingSites.map((v, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                <XCircle className="h-3.5 w-3.5 text-slate-400" />
                <span>{v.site_name} ({v.status})</span>
              </div>
            ))}
          </div>
          <Textarea
            data-testid="complete-notes-input"
            placeholder="Notes (optional)"
            value={completeNotes}
            onChange={e => setCompleteNotes(e.target.value)}
            rows={2}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteDialog(false)}>Continue Patrol</Button>
            <Button
              data-testid="force-complete-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => handleComplete(true)}
              disabled={actionLoading === 'complete'}
            >
              Complete Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Geofence Override Dialog */}
      <Dialog open={!!geofenceOverrideDialog} onOpenChange={(open) => { if (!open) { setGeofenceOverrideDialog(null); setGeofenceOverrideReason(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-amber-600" /> Geofence Blocked</DialogTitle>
            <DialogDescription>You appear to be outside the site geofence. Provide a reason to override and continue check-in.</DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="geofence-override-reason-input"
            placeholder="Why are you checking in outside the geofence? (min 10 characters)"
            value={geofenceOverrideReason}
            onChange={e => setGeofenceOverrideReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setGeofenceOverrideDialog(null); setGeofenceOverrideReason(''); }}>Cancel</Button>
            <Button
              data-testid="geofence-override-confirm-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => handleCheckIn(geofenceOverrideDialog, geofenceOverrideReason)}
              disabled={geofenceOverrideReason.trim().length < 10 || !!actionLoading}
            >
              Override & Check In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
